import { useCallback, useEffect, useRef, useState } from 'react'
import { AssinaturaType, ControlError, getApiErrorMessage } from '_metronic/helpers'
import { ClearanceDto, useAuth } from 'app/modules/auth'
import { refreshTokenForSignature, validateToken } from 'app/modules/auth/core/_requests'
import { loadTipoCert, saveTipoCert } from 'app/modules/auth/core/AuthHelpers'
import { checkCredential, getClearances } from 'app/modules/public/certificado/core/_request'

/**
 * - `verificando`: consultando se o certificado já está liberado.
 * - `senha`: A1 sem token de assinatura; falta a senha.
 * - `provedor`: em nuvem sem credencial autorizada; falta escolher o provedor.
 * - `aguardando`: provedor escolhido, esperando a autorização no celular.
 * - `pronto`: pode assinar.
 */
export type EstadoCertificado = 'verificando' | 'senha' | 'provedor' | 'aguardando' | 'pronto'

const INTERVALO_CREDENCIAL_MS = 5000

/**
 * Prepara o certificado para assinar, sem modais próprios: a tela de assinatura mostra cada
 * estado. A1 pede a senha só quando o token ainda não está liberado para assinatura, e a valida
 * antes de qualquer documento ser gravado. Em nuvem (A3) não há senha: a autorização é no app
 * do provedor, e a tela consulta a credencial até ela ficar válida.
 */
export const useCertificadoAssinatura = (ativo: boolean) => {
  const { currentUser, auth, saveAuth } = useAuth()
  const possuiA1 = !!currentUser?.pessoa?.certificadoDigital

  const opcoes = possuiA1 ? [AssinaturaType.A1, AssinaturaType.A3_Nuvem] : [AssinaturaType.A3_Nuvem]
  const [tipo, setTipo] = useState<AssinaturaType>(() => {
    const salvo = loadTipoCert()
    return possuiA1 && salvo ? salvo : possuiA1 ? AssinaturaType.A1 : AssinaturaType.A3_Nuvem
  })
  const [estado, setEstado] = useState<EstadoCertificado>('verificando')
  const [provedores, setProvedores] = useState<ClearanceDto[]>([])
  const [urlProvedor, setUrlProvedor] = useState<string>()
  const [erro, setErro] = useState<string>()
  const [senhaIncorreta, setSenhaIncorreta] = useState(false)
  const [enviando, setEnviando] = useState(false)

  const accessToken = auth?.accessToken
  // A verificação usa o token da hora, mas não reexecuta quando a senha troca o token.
  const tokenRef = useRef(accessToken)
  tokenRef.current = accessToken

  // Ao abrir ou trocar o tipo, descobre o que falta para assinar.
  useEffect(() => {
    if (!ativo) return
    let cancelado = false
    setEstado('verificando')
    setErro(undefined)
    setSenhaIncorreta(false)
    setUrlProvedor(undefined)

    const verificar = async () => {
      try {
        if (tipo === AssinaturaType.A1) {
          const token = tokenRef.current
          const validacao = token ? await validateToken({ token }) : undefined
          if (!cancelado) setEstado(validacao?.valido && validacao.assinatura ? 'pronto' : 'senha')
          return
        }
        if (await checkCredential()) {
          if (!cancelado) setEstado('pronto')
          return
        }
        const lista = await getClearances()
        if (!cancelado) {
          setProvedores(lista ?? [])
          setEstado('provedor')
        }
      } catch (error) {
        if (!cancelado) {
          setErro(getApiErrorMessage(error) ?? (error as Error)?.message)
          setEstado(tipo === AssinaturaType.A1 ? 'senha' : 'provedor')
        }
      }
    }
    verificar()
    return () => {
      cancelado = true
    }
  }, [ativo, tipo])

  // Em nuvem, com o provedor aberto, consulta a credencial até a autorização chegar. A próxima
  // consulta só é agendada quando a anterior termina, para não empilhar chamadas lentas.
  useEffect(() => {
    if (estado !== 'aguardando') return
    let cancelado = false
    let timer: ReturnType<typeof setTimeout>
    const consultar = async () => {
      try {
        if (await checkCredential()) {
          if (!cancelado) setEstado('pronto')
          return
        }
      } catch (error) {
        if (!cancelado) setErro(getApiErrorMessage(error) ?? (error as Error)?.message)
      }
      if (!cancelado) timer = setTimeout(consultar, INTERVALO_CREDENCIAL_MS)
    }
    timer = setTimeout(consultar, INTERVALO_CREDENCIAL_MS)
    return () => {
      cancelado = true
      clearTimeout(timer)
    }
  }, [estado])

  const escolherTipo = useCallback((novo: AssinaturaType) => setTipo(novo), [])

  const escolherProvedor = useCallback((url?: string) => {
    setUrlProvedor(url)
    setEstado(url ? 'aguardando' : 'provedor')
  }, [])

  /** Libera o token para assinar com a senha do A1. `false` mantém a tela na senha. */
  const confirmarSenha = useCallback(
    async (senha: string) => {
      if (!accessToken) return false
      setEnviando(true)
      setErro(undefined)
      setSenhaIncorreta(false)
      try {
        const novoAuth = await refreshTokenForSignature({ senha, token: accessToken })
        saveAuth(novoAuth)
        setEstado('pronto')
        return true
      } catch (error) {
        // Senha errada vem como 400 com esta mensagem (`PasswordNotMatchException`). Rede, 500 ou
        // certificado vencido mostram a mensagem real: o médico não fica redigitando a senha certa.
        const mensagem = getApiErrorMessage(error) ?? (error as Error)?.message
        if (mensagem === ControlError.InvalidPassword) setSenhaIncorreta(true)
        else setErro(mensagem)
        return false
      } finally {
        setEnviando(false)
      }
    },
    [accessToken, saveAuth]
  )

  /** Guarda a escolha para a próxima assinatura, como o restante do sistema faz. */
  const lembrarTipo = useCallback(() => saveTipoCert(tipo), [tipo])

  return {
    opcoes,
    tipo,
    estado,
    provedores,
    urlProvedor,
    erro,
    senhaIncorreta,
    enviando,
    escolherTipo,
    escolherProvedor,
    confirmarSenha,
    lembrarTipo,
  }
}
